/**
 * Where in the site we are, kept in the URL fragment.
 *
 * **Why not a router.** The plan flagged this as the point where a real router might be earned: the
 * fantasy pages nest a league and a season in the address, which the old one-value hash was
 * explicitly not built for. Adopting TanStack Router would mean restructuring five working pages
 * that have exactly one destination each, to gain nothing they need — so the hash stays and learns
 * to carry segments instead. If a third level of nesting ever appears, revisit it then.
 *
 * A fragment rather than a path because the API and the SPA share an origin and the server already
 * falls back to index.html — but more usefully, it means a page can be linked to and a reload keeps
 * you where you were.
 */

import { useEffect, useState } from 'react'

export type Tab = 'hub' | 'favourites' | 'fantasy' | 'bets' | 'model' | 'tournaments' | 'schedule'

/**
 * Tab order is by how often a page is wanted, not by when it was built.
 *
 * `unlisted` keeps a finished page out of the navigation while leaving it reachable at its own
 * fragment. Not a feature flag and not dead code behind a branch: the route renders, the checks
 * still drive it, and launching is deleting one property — whereas a page commented out or gated on
 * an environment variable rots quietly until nobody trusts it enough to turn on.
 *
 * Favourites is unlisted for now, and belongs second when it launches: it is about the same
 * tournament as the first tab and answers the question a reader arrives with — who is actually good
 * — which no page of individual matches does.
 */
export const TABS: { id: Tab; label: string; unlisted?: boolean }[] = [
  { id: 'hub', label: 'The International 2026' },
  { id: 'favourites', label: 'Favourites', unlisted: true },
  { id: 'fantasy', label: 'Fantasy League' },
  { id: 'bets', label: 'Bet record' },
  { id: 'model', label: 'Model' },
  { id: 'tournaments', label: 'Tournaments' },
  { id: 'schedule', label: 'All matches' },
]

// Every tab, listed or not. An unlisted page must still survive a reload and a shared link —
// resolving its fragment to the default would make the page unreachable rather than merely quiet.
const isTab = (value: string): value is Tab => TABS.some((t) => t.id === value)

export interface Route {
  tab: Tab
  /** Everything after the tab, e.g. `['3', '7', 'leaderboard']`. Empty for a flat page. */
  segments: string[]
}

function read(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '')
  const [first, ...rest] = raw.split('/').filter((part) => part.length > 0)

  return {
    tab: first !== undefined && isTab(first) ? first : 'hub',
    segments: rest,
  }
}

export function useRoute(): [Route, (tab: Tab, ...segments: (string | number)[]) => void] {
  const [route, setRoute] = useState<Route>(read)

  useEffect(() => {
    const onChange = () => setRoute(read())
    window.addEventListener('hashchange', onChange)
    return () => window.removeEventListener('hashchange', onChange)
  }, [])

  return [
    route,
    (tab: Tab, ...segments: (string | number)[]) => {
      // Writing the hash fires hashchange, which sets state — one path in, so back and forward
      // behave without any extra bookkeeping.
      const next = [tab, ...segments].join('/')
      const target = `#/${next}`

      if (window.location.hash === target) {
        // Assigning an unchanged hash fires no event, so a "go here" that is already here would
        // otherwise do nothing at all — which matters for a retry button.
        setRoute(read())
        return
      }

      window.location.hash = target
    },
  ]
}

/** A number from a route segment, or null when it is missing or not one. */
export function segmentId(segments: string[], index: number): number | null {
  const raw = segments[index]
  if (raw === undefined) return null
  const value = Number.parseInt(raw, 10)
  return Number.isFinite(value) ? value : null
}
